/* eslint-disable no-param-reassign */
import * as Sentry from '@sentry/vue'
import ReadinessCheckerApi from '@/api/readinessChecker'
import kvSyncSubscriber from '@/mqtt/kvSync'
// import { isFeatureEnabled } from '@/vuex/modules/featureToggles.module'

const SET_POTENTIAL_ISSUES = 'SET_POTENTIAL_ISSUES'
const ADD_POTENTIAL_ISSUES = 'ADD_POTENTIAL_ISSUES'
const SET_IS_TRIGGERING = 'SET_IS_TRIGGERING'
const SET_TRIGGER_FAILED = 'SET_TRIGGER_FAILED'
const REMOVE_ISSUE = 'REMOVE_ISSUE'
const SET_LAST_CHECKED = 'SET_LAST_CHECKED'

const _state = {
  potentialIssues: [],
  isTriggering: false,
  triggerFailed: false,
  lastChecked: null,
}

const _getters = {
  potentialIssues(state) {
    return state.potentialIssues
  },

  isTriggering(state) {
    return state.isTriggering
  },

  triggerFailed(state) {
    return state.triggerFailed
  },

  lastChecked(state) {
    return state.lastChecked
  },

  canUseReadinessChecker: (_, __, ___, rootGetters) => {
    return rootGetters['featureToggles/isFeatureEnabled']('release-AHA-40468-readiness-checker')
  },

  issuesByType: (state) => (type) => {
    return state.potentialIssues.filter((issue) => issue.type === type)
  },

  issuesByScope: (state) => (scope) => {
    return state.potentialIssues.filter((issue) => issue.scope === scope)
  },

  issuesForSlide: (state) => (slideId) => {
    return state.potentialIssues.filter(
      (issue) => issue.scope === 'slide' && issue.slideId === slideId,
    )
  },

  issueCount(state) {
    return state.potentialIssues.length
  },
}

const mutations = {
  [SET_POTENTIAL_ISSUES](state, issues) {
    state.potentialIssues = issues
  },

  [ADD_POTENTIAL_ISSUES](state, issues) {
    if (
      state.potentialIssues.length > 0 &&
      issues.some(
        (issue) => issue.createdAt > state.potentialIssues[0].createdAt,
      )
    ) {
      state.potentialIssues = issues
      state.lastChecked = issues[0].createdAt
    } else {
      state.potentialIssues = [...state.potentialIssues, ...issues]
      if (!state.lastChecked) {
        state.lastChecked = issues[0].createdAt
      }
    }
  },

  [SET_IS_TRIGGERING](state, { isTriggering }) {
    state.isTriggering = isTriggering
  },

  [SET_TRIGGER_FAILED](state, { triggerFailed }) {
    state.triggerFailed = triggerFailed
  },

  [REMOVE_ISSUE](state, { issueId }) {
    const issueIndex = state.potentialIssues.findIndex((issue, index) => index === issueId)
    if (issueIndex >= 0) {
      state.potentialIssues.splice(issueIndex, 1)
    }
  },

  [SET_LAST_CHECKED](state, lastChecked) {
    state.lastChecked = lastChecked
  },
}

const getReadinessCheckerTopic = (presentationId) => {
  return `presentation-${presentationId}-readiness-checker`
}

const actions = {
  subscribeReadinessChecker({ dispatch }, { presentationId }) {
    if (!presentationId) return
    const topic = getReadinessCheckerTopic(presentationId)
    kvSyncSubscriber.subscribeKey(topic, (_, message) => {
      try {
        const { issues } = JSON.parse(message)
        dispatch('handleReadinessCheckerResultAction', {
          potentialIssues: issues || [],
        })
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'readiness-checker-mqtt' },
        })
      }
    })
  },

  unsubscribeReadinessChecker(_, { presentationId }) {
    if (!presentationId) return
    kvSyncSubscriber.unsubscribeKey(getReadinessCheckerTopic(presentationId))
  },

  async triggerReadinessCheckerAction({ commit, rootState }) {
    try {
      commit(SET_TRIGGER_FAILED, { triggerFailed: false })

      const presentationId = rootState.presentation?.id
      if (!presentationId) {
        return
      }

      commit(SET_IS_TRIGGERING, { isTriggering: true })

      await ReadinessCheckerApi.triggerReadinessChecker({ presentationId })
    } catch (error) {
      commit(SET_IS_TRIGGERING, { isTriggering: false })
      commit(SET_TRIGGER_FAILED, { triggerFailed: true })

      Sentry.captureException(error, {
        tags: { feature: 'readiness-checker-trigger' },
        extra: { error },
      })
    }
  },

  async getReadinessPotentialIssuesAction({ commit, rootState }) {
    const presentationId = rootState.presentation?.id
    if (!presentationId) {
      return
    }
    const potentialIssues = await ReadinessCheckerApi.getReadinessPotentialIssues({ presentationId })
    if (potentialIssues && potentialIssues.length > 0) {
      commit(ADD_POTENTIAL_ISSUES, potentialIssues)
    }
    commit(SET_IS_TRIGGERING, { isTriggering: false })
  },

  handleReadinessCheckerResultAction({ commit }, { potentialIssues }) {
    if (potentialIssues && potentialIssues.length > 0) {
      commit(ADD_POTENTIAL_ISSUES, potentialIssues)
    } else {
      commit(SET_IS_TRIGGERING, { isTriggering: false })
    }
  },

  resetReadinessCheckerAction({ commit }) {
    commit(SET_POTENTIAL_ISSUES, [])
    commit(SET_IS_TRIGGERING, { isTriggering: false })
    commit(SET_TRIGGER_FAILED, { triggerFailed: false })
    commit(SET_LAST_CHECKED, null)
  },

  dismissIssueAction({ commit }, { issueId }) {
    commit(REMOVE_ISSUE, { issueId })
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
